"use client";
import React from "react";
import { config } from "@/lib/config";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (href: string) => {
    if (href.includes('#')) {
      // Handle anchor links
      const [path, anchor] = href.split('#');
      if (path === '/' || path === '') {
        // If we're already on the home page, just scroll to anchor
        if (location.pathname === '/') {
          const element = document.getElementById(anchor);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          } else if (anchor === 'top') {
            // Scroll to top of page
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } else {
          // Navigate to home page first, then scroll to anchor
          navigate('/');
          setTimeout(() => {
            const element = document.getElementById(anchor);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            } else if (anchor === 'top') {
              // Scroll to top of page
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 100);
        }
      } else {
        navigate(href);
      }
    } else {
      navigate(href);
    }
    setOpen(false);
  };

  const handleLinkClick = (href: string) => {
    if (href.includes('#')) {
      // Handle anchor links
      const [path, anchor] = href.split('#');
      if (path === '/' || path === '') {
        // If we're already on the home page, just scroll to anchor
        if (location.pathname === '/') {
          const element = document.getElementById(anchor);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          } else if (anchor === 'top') {
            // Scroll to top of page
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } else {
          // Navigate to home page first, then scroll to anchor
          navigate('/');
          setTimeout(() => {
            const element = document.getElementById(anchor);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            } else if (anchor === 'top') {
              // Scroll to top of page
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 100);
        }
      } else {
        navigate(href);
      }
    }
  };

  return (
    <header className="sticky top-0 z-20 flex h-[60px] bg-amber-400 bg-opacity-30 align-middle shadow-md backdrop-blur-md">
      <div>
        <Link to="/">
          <img
            src={`${config.basePath}/images/beart-transparent-logo.webp`}
            alt="BeArt"
            width={120}
            height={60}
          />
        </Link>
      </div>
      <div className="my-auto ml-8 hidden w-full text-center align-middle text-lg text-black md:inline-block">
        <button
          onClick={() => handleLinkClick("/#top")}
          className="text-nowrap p-4 hover:bg-amber-400 hover:text-red-900"
        >
          <i className="bi bi-brush mr-2"></i> Művészetterápia
        </button>
        <button
          onClick={() => handleLinkClick("/#coaching")}
          className="text-nowrap p-4 hover:bg-amber-400 hover:text-red-900"
        >
          <i className="bi bi-palette mr-2"></i> Art coaching
        </button>
        <Link
          to="/lelek-szinei"
          className="text-nowrap p-4 hover:bg-amber-400 hover:text-red-900"
        >
          <i className="bi bi-easel mr-2"></i> Kiállítás
        </Link>
        <Link
          to="/galeria"
          className="text-nowrap p-4 hover:bg-amber-400 hover:text-red-900"
        >
          <i className="bi bi-images mr-2"></i>Galéria
        </Link>
        <button
          onClick={() => handleLinkClick("/#szervezet")}
          className="text-nowrap p-4 hover:bg-amber-400 hover:text-red-900"
        >
          <i className="bi bi-building mr-2"></i>Szervezeteknek
        </button>
        <Link
          to="/events"
          className="text-nowrap p-4 hover:bg-amber-400 hover:text-red-900"
        >
          <i className="bi bi-calendar-week mr-2"></i>Események
        </Link>
        <Link
          to="/kapcsolat"
          className="text-nowrap p-4 hover:bg-amber-400 hover:text-red-900"
        >
          <i className="bi bi-chat-text mr-2"></i>Kapcsolat
        </Link>
      </div>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger className="block md:hidden">
          <div className="absolute right-0 -my-4 mr-4">
            <i className="bi bi-list text-2xl"></i>
          </div>
        </DrawerTrigger>
        <DrawerContent className="fixed bottom-0 right-0 mt-24 flex h-full w-full flex-col rounded-t-[10px] bg-amber-100 shadow-md backdrop-blur-md">
          <DrawerHeader>
            <DrawerTitle>
              <div className="flex w-full items-center justify-center text-center">
                <img
                  src={`${config.basePath}/images/beart-transparent-logo.png`}
                  alt="BeArt"
                  width={240}
                  height={120}
                />
              </div>
            </DrawerTitle>
            <DrawerDescription></DrawerDescription>
            <div className="mt-8 block text-center align-middle text-lg text-black">
              <div className="w-full">
                <Button
                  onClick={() => handleNavigate("/#top")}
                  className="mb-4 w-full text-nowrap bg-amber-100 p-4 text-2xl text-black hover:bg-amber-400 hover:text-red-900"
                >
                  <i className="bi bi-brush mr-2"></i> Művészetterápia
                </Button>
              </div>
              <div>
                <Button
                  onClick={() => handleNavigate("/#coaching")}
                  className="mb-4 w-full text-nowrap bg-amber-100 p-4 text-2xl text-black hover:bg-amber-400 hover:text-red-900"
                >
                  <i className="bi bi-palette mr-2"></i> Art coaching
                </Button>
              </div>
              <div>
                <Button
                  onClick={() => handleNavigate("/lelek-szinei")}
                  className="mb-4 w-full text-nowrap bg-amber-100 p-4 text-2xl text-black hover:bg-amber-400 hover:text-red-900"
                >
                  <i className="bi bi-easel mr-2"></i> Kiállítás
                </Button>
              </div>
              <div>
                <Button
                  onClick={() => handleNavigate("/galeria")}
                  className="mb-4 w-full text-nowrap bg-amber-100 p-4 text-2xl text-black hover:bg-amber-400 hover:text-red-900"
                >
                  <i className="bi bi-images mr-2"></i> Galéria
                </Button>
              </div>
              <div>
                <Button
                  onClick={() => handleNavigate("/#szervezet")}
                  className="mb-4 w-full text-nowrap bg-amber-100 p-4 text-2xl text-black hover:bg-amber-400 hover:text-red-900"
                >
                  <i className="bi bi-building mr-2"></i>Szervezeteknek
                </Button>
              </div>
              <div>
                <Button
                  onClick={() => handleNavigate("/events")}
                  className="mb-4 w-full text-nowrap bg-amber-100 p-4 text-2xl text-black hover:bg-amber-400 hover:text-red-900"
                >
                  <i className="bi bi-calendar-week mr-2"></i>Események
                </Button>
              </div>
              <div>
                <Button
                  onClick={() => handleNavigate("/kapcsolat")}
                  className="mb-4 w-full text-nowrap bg-amber-100 p-4 text-2xl text-black hover:bg-amber-400 hover:text-red-900"
                >
                  <i className="bi bi-chat-text mr-2"></i>Kapcsolat
                </Button>
              </div>
            </div>
          </DrawerHeader>
          <DrawerFooter>
            <DrawerClose>
              <i className="bi bi-x-circle text-2xl"></i>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </header>
  );
}
